import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Compass } from "lucide-react";
import { EditionShell } from "../components/dnh/feed/EditionShell";
import { WorldPulseSection } from "../components/dnh/home/LiveIntelligenceMapSection";
import { PlatformStatsCtx } from "../components/dnh/home/PlatformStatsContext";
import { getStats, getMapLive } from "../utils/api";
import { ExploreDirectory } from "../components/dnh/feed/ExploreDirectory";

const views = {
  map:        "World map",
  countries:  "Countries",
  states:     "States & regions",
  categories: "Categories",
  topics:     "Topics",
};

export default function Explore() {
  const [params, setParams] = useSearchParams();
  const view = views[params.get("view")] ? params.get("view") : "map";

  const [stats,   setStats]   = useState(null);
  const [mapData, setMapData] = useState(null);

  // Fetch stats + live map data once on mount — used by both the map view
  // (via PlatformStatsCtx) and the directory views (for signal badges).
  useEffect(() => {
    let live = true;
    getStats().then(d => { if (live) setStats(d); }).catch(() => {});
    getMapLive().then(d => { if (live) setMapData(d); }).catch(() => {});
    return () => { live = false; };
  }, []);

  const change = value => {
    const next = new URLSearchParams(params);
    next.set("view", value);
    next.delete("group");
    setParams(next);
  };

  return (
    <EditionShell
      title="Explore"
      activeNav="Explore"
      testId="explore-page"
      actions={
        <Link to="/see-through-lens" className="dnh-outline-button gap-2" data-testid="explore-lens-link">
          <Compass size={16} />Perspectives
        </Link>
      }
    >
      {/* View tabs */}
      <div className="border-b border-[var(--hairline)] pb-4 mb-6" data-testid="explore-tabs">
        <select
          value={view}
          onChange={e => change(e.target.value)}
          aria-label="Explore section"
          className="dnh-select w-full sm:hidden"
          data-testid="explore-view-select"
        >
          {Object.entries(views).map(([key, label]) => <option value={key} key={key}>{label}</option>)}
        </select>
        <div className="hidden sm:flex items-center gap-1">
          {Object.entries(views).map(([key, label]) => (
            <button
              key={key}
              onClick={() => change(key)}
              aria-pressed={view === key}
              className={`min-h-11 px-4 text-xs ${view === key ? "bg-[var(--ink)] text-[var(--paper)]" : "hover:bg-[var(--surface-2)]"}`}
              data-testid={`explore-tab-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {view === "map"
        ? (
          <PlatformStatsCtx.Provider value={stats}>
            <div data-testid="explore-only-world-map">
              <WorldPulseSection />
            </div>
          </PlatformStatsCtx.Provider>
        )
        : <ExploreDirectory view={view} mapData={mapData} />
      }
    </EditionShell>
  );
}
